<template>
  <div v-if="visible" class="wl-add-to-queue-sidebar">
    <!-- Backdrop -->
    <transition name="wl-sidebar-fade">
      <div
        v-if="showContent"
        class="wl-sidebar-backdrop"
        @click="close"
      ></div>
    </transition>

    <!-- Panel -->
    <transition name="wl-sidebar-slide">
      <div
        v-if="showContent"
        class="wl-sidebar-panel"
      >
        <!-- Header -->
        <div class="wl-sidebar-header">
          <h5 class="wl-sidebar-title">{{ $t('$WaitingList.AddToQueue.Title') }}</h5>
          <button
            type="button"
            class="wl-sidebar-close"
            @click="close"
          >
            <feather-icon icon="XIcon" svgClasses="h-5 w-5" />
          </button>
        </div>

        <!-- Tabs (vs-tabs style) -->
        <vs-tabs
          v-model="activeTab"
          position="top"
          class="wl-sidebar-vs-tabs tabs-shadow-none"
        >
          <vs-tab
            icon-pack="feather"
            icon="icon-zap"
            :label="$t('$WaitingList.AddToQueue.Tabs.QuickAdd')"
          >
            <div class="hidden"></div>
          </vs-tab>
          <vs-tab
            icon-pack="feather"
            icon="icon-maximize"
            :label="$t('$WaitingList.AddToQueue.Tabs.SelfCheckIn')"
          >
            <div class="hidden"></div>
          </vs-tab>
          <vs-tab
            icon-pack="feather"
            icon="icon-users"
            :label="$t('$WaitingList.AddToQueue.Tabs.BulkAdd')"
          >
            <div class="hidden"></div>
          </vs-tab>
        </vs-tabs>

        <!-- Tab content -->
        <div class="wl-sidebar-body">
          <!-- Quick add -->
          <div v-show="activeTab === 0">
            <form @submit.prevent="submitQuickAdd">
              <div class="flex gap-3 mb-4">
                <div class="flex-1">
                  <label class="wl-form-label">{{ $t('$General.FirstName') }}</label>
                  <vs-input
                    v-model="quickAdd.firstName"
                    :placeholder="$t('$WaitingList.AddToQueue.Placeholders.FirstName')"
                    class="w-full"
                  />
                </div>
                <div class="flex-1">
                  <label class="wl-form-label">{{ $t('$General.LastName') }}</label>
                  <vs-input
                    v-model="quickAdd.lastName"
                    :placeholder="$t('$WaitingList.AddToQueue.Placeholders.LastName')"
                    class="w-full"
                  />
                </div>
              </div>

              <div class="mb-4">
                <label class="wl-form-label">{{ $tc('$General.Email', 1) }}</label>
                <vs-input
                  v-model="quickAdd.email"
                  :placeholder="$t('$WaitingList.AddToQueue.Placeholders.Email')"
                  type="email"
                  class="w-full"
                />
              </div>

              <div class="mb-4">
                <label class="wl-form-label">{{ $tc('$General.PhoneNumber', 1) }}</label>
                <vue-tel-input
                  v-model="quickAdd.phoneNational"
                  :disabledFetchingCountry="true"
                  :dynamicPlaceholder="false"
                  :enabledCountryCode="true"
                  :validCharactersOnly="true"
                  :preferredCountries="['AU', 'CO']"
                  defaultCountry="AU"
                  autocomplete="tel"
                  @validate="(payload) => (quickAdd.phoneValidate = payload)"
                  @input="() => (quickAdd.phoneInput = true)"
                  @blur="() => (quickAdd.phoneInput = true)"
                />
                <span
                  v-show="invalidPhoneNumber"
                  class="text-danger text-sm"
                >
                  {{ $t('$WaitingList.AddToQueue.Errors.PhoneNumberInvalid') }}
                </span>
              </div>

              <vs-button
                class="w-full"
                :disabled="!quickAdd.firstName || invalidPhoneNumber || quickAddLoading"
                @click="submitQuickAdd"
              >
                <feather-icon icon="PlusIcon" svgClasses="h-4 w-4" class="mr-1" />
                {{ $t('$General.Add') }}
              </vs-button>
            </form>
          </div>

          <!-- Self-check-in -->
          <div v-show="activeTab === 1">
            <div class="wl-qr-frame">
              <waiting-list-q-r-code
                ref="qrCode"
                :size="200"
                compact
              />
            </div>

            <vs-button
              type="border"
              class="w-full mt-4"
              @click="downloadQR"
            >
              <feather-icon icon="DownloadIcon" svgClasses="h-4 w-4" class="mr-2" />
              {{ $t('$WaitingList.AddToQueue.Buttons.DownloadQR') }}
            </vs-button>

            <vs-button
              type="border"
              class="w-full mt-3"
              @click="copyLink"
            >
              <feather-icon icon="CopyIcon" svgClasses="h-4 w-4" class="mr-2" />
              {{ $t('$WaitingList.AddToQueue.Buttons.CopyLink') }}
            </vs-button>

            <vs-button
              type="border"
              class="w-full mt-3"
              @click="openKiosk"
            >
              <feather-icon icon="MonitorIcon" svgClasses="h-4 w-4" class="mr-2" />
              {{ $t('$WaitingList.AddToQueue.Buttons.OpenKiosk') }}
            </vs-button>
          </div>

          <!-- Bulk add -->
          <div v-show="activeTab === 2">
            <div>
              <label class="wl-form-label">{{ $t('$WaitingList.AddToQueue.NumberOfPositions') }}</label>
              <vs-input
                v-model.number="bulkCount"
                type="number"
                min="1"
                max="50"
                class="w-full"
              />
            </div>

            <vs-button
              type="border"
              class="w-full mt-4"
              :disabled="!bulkCount || bulkCount < 1 || bulkAddLoading"
              @click="submitBulkAdd"
            >
              <feather-icon icon="CopyIcon" svgClasses="h-4 w-4" class="mr-2" />
              {{ $t('$WaitingList.AddToQueue.Buttons.ReservePositions') }}
            </vs-button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { VueTelInput } from 'vue-tel-input';
import WaitingListQRCode from '@/views/modules/waiting-list/WaitingListQRCode.vue';
import waitingListService from '@/api/modules/waiting-list.service';

export default {
  name: 'WaitingListAddToQueueSidebar',
  components: {
    WaitingListQRCode,
    VueTelInput,
  },
  props: {
    active: {
      type: Boolean,
      default: false,
    },
    initialTab: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      visible: false,
      showContent: false,
      activeTab: this.initialTab,
      quickAdd: {
        firstName: '',
        lastName: '',
        email: '',
        phoneNational: '',
        phoneValidate: { isValid: false },
        phoneInput: false,
      },
      quickAddLoading: false,
      bulkCount: 1,
      bulkAddLoading: false,
      portalTarget: null,
    };
  },
  computed: {
    ...mapGetters({
      tenantUrlLinksSuffix: 'auth/tenantUrlLinksSuffix',
    }),
    invalidPhoneNumber() {
      return this.quickAdd.phoneInput
        && this.quickAdd.phoneNational !== ''
        && !this.quickAdd.phoneValidate.isValid;
    },
    kioskUrl() {
      const dashboardUrl = this.$enums.AppLinks.DASHBOARD;
      const baseUrl = dashboardUrl.endsWith('/') ? dashboardUrl : `${dashboardUrl}/`;
      return `${baseUrl}waitlist/kiosk/${this.tenantUrlLinksSuffix}`;
    },
  },
  watch: {
    active(val) {
      if (val) {
        this.open();
      } else {
        this.animateClose();
      }
    },
    initialTab(val) {
      this.activeTab = val;
    },
  },
  mounted() {
    // Portal: move component DOM to body to escape z-index stacking context
    this.portalTarget = document.createElement('div');
    document.body.appendChild(this.portalTarget);
    this.portalTarget.appendChild(this.$el);

    if (this.active) {
      this.open();
    }
  },
  beforeDestroy() {
    if (this.portalTarget) {
      if (this.portalTarget.parentNode) {
        this.portalTarget.parentNode.removeChild(this.portalTarget);
      }
    }
    document.body.classList.remove('wl-sidebar-open');
  },
  methods: {
    open() {
      this.activeTab = this.initialTab;
      this.visible = true;
      document.body.classList.add('wl-sidebar-open');
      this.$nextTick(() => {
        this.showContent = true;
      });
    },
    animateClose() {
      this.showContent = false;
      document.body.classList.remove('wl-sidebar-open');
      setTimeout(() => {
        this.visible = false;
      }, 300);
    },
    close() {
      this.$emit('update:active', false);
    },
    resetQuickAddForm() {
      this.quickAdd = {
        firstName: '',
        lastName: '',
        email: '',
        phoneNational: '',
        phoneValidate: { isValid: false },
        phoneInput: false,
      };
    },
    getPhoneInternationalE164() {
      const v = this.quickAdd.phoneValidate;
      if (v && v.number && v.number.e164) {
        return v.number.e164;
      }
      return '';
    },
    async submitQuickAdd() {
      if (!this.quickAdd.firstName) return;

      // If a phone number has been entered, it must be valid
      if (this.quickAdd.phoneNational && !this.quickAdd.phoneValidate.isValid) {
        this.quickAdd.phoneInput = true;
        return;
      }

      this.quickAddLoading = true;
      try {
        const payload = {
          firstName: this.quickAdd.firstName,
        };
        if (this.quickAdd.lastName) payload.lastName = this.quickAdd.lastName;
        if (this.quickAdd.email) payload.email = this.quickAdd.email;

        const phoneE164 = this.getPhoneInternationalE164();
        if (phoneE164) payload.phoneInternational = phoneE164;

        await waitingListService.manualAdd(payload);

        this.$vs.notify({
          title: this.$t('$WaitingList.AddToQueue.Notifications.AddedToQueue'),
          text: this.$t('$WaitingList.AddToQueue.Notifications.AddedToQueueText', { name: this.quickAdd.firstName }),
          color: 'success',
          iconPack: 'feather',
          icon: 'icon-check',
        });
        this.resetQuickAddForm();
        this.$emit('added');
        this.close();
      } catch (error) {
        const msg = error?.response?.data?.message || this.$t('$WaitingList.AddToQueue.Notifications.AddError');
        this.$vs.notify({
          title: this.$t('$General.Error'),
          text: msg,
          color: 'danger',
          iconPack: 'feather',
          icon: 'icon-alert-circle',
        });
      } finally {
        this.quickAddLoading = false;
      }
    },
    async submitBulkAdd() {
      if (!this.bulkCount || this.bulkCount < 1) return;

      this.bulkAddLoading = true;
      try {
        await waitingListService.reservePositions({ count: this.bulkCount });

        this.$vs.notify({
          title: this.$t('$WaitingList.AddToQueue.Notifications.PositionsReserved'),
          text: this.$tc('$WaitingList.AddToQueue.Notifications.PositionsReservedText', this.bulkCount, { count: this.bulkCount }),
          color: 'success',
          iconPack: 'feather',
          icon: 'icon-check',
        });
        this.bulkCount = 1;
        this.$emit('added');
        this.close();
      } catch (error) {
        const msg = error?.response?.data?.message || this.$t('$WaitingList.AddToQueue.Notifications.ReserveError');
        this.$vs.notify({
          title: this.$t('$General.Error'),
          text: msg,
          color: 'danger',
          iconPack: 'feather',
          icon: 'icon-alert-circle',
        });
      } finally {
        this.bulkAddLoading = false;
      }
    },
    downloadQR() {
      if (this.$refs.qrCode) {
        this.$refs.qrCode.downloadAsImage();
      }
    },
    copyLink() {
      if (this.$refs.qrCode) {
        this.$refs.qrCode.copyLink();
      }
    },
    openKiosk() {
      window.open(this.kioskUrl, '_blank');
      this.close();
    },
  },
};
</script>

<style lang="scss">
/* NOT scoped — namespaced under .wl-add-to-queue-sidebar */
.wl-add-to-queue-sidebar {
  /* Backdrop */
  .wl-sidebar-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    z-index: 52000;
  }

  /* Panel */
  .wl-sidebar-panel {
    position: fixed;
    top: 0;
    right: 0;
    width: 400px;
    height: 100%;
    background: #fff;
    z-index: 52001;
    display: flex;
    flex-direction: column;
    box-shadow: -2px 0 8px rgba(0, 0, 0, 0.15);

    @media (max-width: 768px) {
      width: 100%;
    }
  }

  /* Header */
  .wl-sidebar-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid #e4e7eb;
    flex-shrink: 0;
  }

  .wl-sidebar-title {
    font-size: 1.15rem;
    font-weight: 600;
    margin: 0;
  }

  .wl-sidebar-close {
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px;
    color: #626262;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      background: #f4f4f4;
    }
  }

  /* Tabs (vs-tabs overrides for sidebar context) */
  .wl-sidebar-vs-tabs {
    flex-shrink: 0;
    padding-top: 4px;

    .con-ul-tabs {
      padding: 0;
    }

    .ul-tabs {
      display: flex;
      padding: 0 20px;
    }

    .vs-tabs--li {
      flex: 1;
    }

    .vs-tabs--btn {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;
      width: 100%;
      padding: 12px 4px 10px;
      font-size: 0.8rem;
    }

    /* Force animated indicator to bottom of tab row */
    .line-vs-tabs {
      bottom: 0;
      top: auto !important;
    }

    /* Hide default tab content area (we render content ourselves) */
    .con-slot-tabs {
      display: none;
    }
  }

  /* Body */
  .wl-sidebar-body {
    flex: 1;
    overflow-y: auto;
    padding: 24px 20px;
  }

  /* Form label */
  .wl-form-label {
    display: block;
    font-size: 0.85rem;
    font-weight: 500;
    color: #626262;
    margin-bottom: 4px;
  }

  /* vue-tel-input: ensure country dropdown renders above sidebar */
  .vue-tel-input {
    .vti__dropdown-list {
      z-index: 53000 !important;
    }
  }

  /* QR frame */
  .wl-qr-frame {
    background: #fff;
    border: 1px solid #e4e7eb;
    border-radius: 8px;
    padding: 24px;
    display: flex;
    justify-content: center;
  }

  /* Gap utility (Vue 2 / older browsers) */
  .gap-3 {
    gap: 0.75rem;
  }

  /* Transitions */
  .wl-sidebar-fade-enter-active,
  .wl-sidebar-fade-leave-active {
    transition: opacity 0.3s ease;
  }
  .wl-sidebar-fade-enter,
  .wl-sidebar-fade-leave-to {
    opacity: 0;
  }

  .wl-sidebar-slide-enter-active,
  .wl-sidebar-slide-leave-active {
    transition: transform 0.3s ease;
  }
  .wl-sidebar-slide-enter,
  .wl-sidebar-slide-leave-to {
    transform: translateX(100%);
  }
}

/* Prevent body scroll when sidebar is open */
body.wl-sidebar-open {
  overflow: hidden;
}
</style>
